import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Users } from "lucide-react";
import { FORMATIONS } from "../data/content";
import { Reveal } from "../components/Reveal";

export default function Formations() {
  const [openDates, setOpenDates] = useState(null);

  return (
    <div data-testid="formations-page">
      <section className="pt-32 lg:pt-40 pb-16 px-6 lg:px-12 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-olive mb-6">Formations à Bayonne</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-ink leading-tight max-w-3xl">Transmettre l'art du soin</h1>
          <p className="mt-8 text-base sm:text-lg text-sage leading-relaxed max-w-2xl">
            Des formations immersives pour développer des compétences en massage thérapeutique, magnétisme et accompagnement énergétique. Un chemin de connaissance de soi autant qu'un apprentissage professionnel.
          </p>
        </Reveal>
      </section>

      <section className="pb-24 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="border-t border-olive-border" data-testid="formations-list">
          {FORMATIONS.map((f, i) => (
            <Reveal key={f.num} delay={i * 0.06}>
              <article className="group grid lg:grid-cols-[6rem_1fr_auto] gap-6 lg:gap-12 items-start border-b border-olive-border py-12 transition-colors duration-300 hover:bg-olive-soft/40 px-3 -mx-3 rounded" data-testid={`formation-${f.num}`}>
                <span className="font-serif text-5xl sm:text-6xl text-olive/25 group-hover:text-olive/50 transition-colors duration-500">{f.num}</span>
                <div>
                  <h2 className="font-serif text-3xl sm:text-4xl text-ink group-hover:text-olive transition-colors duration-300">{f.name}</h2>
                  <p className="mt-4 text-sage leading-relaxed max-w-2xl">{f.description}</p>
                  {f.objectifs && (
                    <div className="mt-5 max-w-2xl">
                      <p className="text-xs uppercase tracking-[0.25em] text-olive mb-3">{f.programmeTitle || "Objectifs de la formation"} — à Bayonne et partout en France</p>
                      <ul className="space-y-2">
                        {f.objectifs.map((o) => (
                          <li key={o} className="flex items-start gap-3 text-sm text-sage leading-relaxed">
                            <span className="text-olive shrink-0">—</span>
                            <span>{o}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {f.extra && <p className="mt-5 text-sage leading-relaxed max-w-2xl italic">{f.extra}</p>}
                  <div className="mt-6 flex flex-wrap gap-3 text-sm text-olive-dark">
                    <span className="inline-flex items-center gap-2 rounded-full border border-olive-border bg-beige-50 px-4 py-2">
                      <CalendarDays className="w-4 h-4" strokeWidth={1.5} /> {f.duration}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-olive-border bg-beige-50 px-4 py-2 font-medium text-olive">
                      {f.price}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-olive-border bg-beige-50 px-4 py-2">
                      <Users className="w-4 h-4" strokeWidth={1.5} /> {f.audience}
                    </span>
                  </div>
                </div>
                <div className="lg:mt-2 flex flex-col items-start gap-3">
                  {f.landing && (
                    <Link
                      to={f.landing}
                      data-testid={`formation-landing-${f.num}`}
                      className="inline-flex items-center gap-2 rounded-full bg-olive px-6 py-3 text-sm font-medium text-beige-100 transition-all duration-300 hover:bg-olive-dark hover:-translate-y-0.5"
                    >
                      Découvrir la formation {f.num === "01" ? "Chi Nei Tsang" : "Réflexologie plantaire"} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpenDates(openDates === f.num ? null : f.num)}
                    data-testid={`formation-cta-${f.num}`}
                    className="inline-flex items-center gap-2 rounded-full border border-olive px-6 py-3 text-sm text-olive transition-all duration-300 hover:bg-olive hover:text-beige-100 hover:-translate-y-0.5"
                  >
                    {f.dates ? `Prochaines dates · ${f.dates}` : "Prochaines dates"} <CalendarDays className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                  </button>
                  {openDates === f.num && (
                    <div className="rounded-2xl border border-olive-border bg-beige-50 p-5 max-w-xs" data-testid={`formation-dates-${f.num}`}>
                      {f.dates ? (
                        <>
                          <p className="text-sm text-olive-dark font-medium">{f.datesDetail || `${f.dates} · Bayonne`}</p>
                          {f.datesNote && <p className="mt-1 text-sm text-sage leading-relaxed">{f.datesNote}</p>}
                          {f.landing && (
                            <Link
                              to={f.landing}
                              data-testid={`formation-dates-cta-${f.num}`}
                              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-olive hover:text-olive-dark transition-colors"
                            >
                              Voir la page de la formation <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                            </Link>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-sage leading-relaxed">Les sessions s'ouvrent au fil des saisons, en petit groupe. Écrivez-moi pour connaître les prochaines dates de « {f.name} » et réserver votre place.</p>
                          <Link
                            to={`/contact?soin=formation-${encodeURIComponent(f.name.toLowerCase().replace(/\s+/g, "-"))}`}
                            data-testid={`formation-dates-cta-${f.num}`}
                            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-olive hover:text-olive-dark transition-colors"
                          >
                            Demander les dates <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-olive-dark text-beige-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl font-light leading-snug">« Chaque individu a en lui les ressources nécessaires pour faire des choix éclairés et s'épanouir. »</h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:justify-self-end">
            <p className="text-beige-300/85 leading-relaxed max-w-md">Les groupes sont volontairement restreints pour un apprentissage pratique et humain. Les dates sont annoncées au fil des saisons — écrivez-moi pour être informé·e.</p>
            <Link to="/contact" data-testid="formations-cta-contact" className="mt-6 inline-flex items-center rounded-full bg-beige-100 px-8 py-4 text-sm font-medium text-olive-dark transition-all duration-300 hover:-translate-y-0.5 hover:bg-beige-50">
              Être informé·e des dates
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
